import {atom} from 'recoil';

import {IEditableFullAddress, IServiceRequest} from '../../shared/types/serviceRequest.types';
import {IPageableDto, IPageableTable} from '../../shared/types/pageable.types';
import {IContentTreeItem, InputTypeEnum} from '../../shared/types/workContent.types';
import {getEmptyPage} from '../../shared/helpers/getEmptyPage';
import {AppAny} from '../../shared/types/common.types';

export const EMPTY_ADDRESS: IEditableFullAddress = {
  region: null,
  street: null,
  district: null,
  city: null,
  postalCode: null,
  house: null,
  building: null,
  apartment: null,
  cadastre: null,
  latitude: null,
  longitude: null,
};

export const EMPTY_SERVICE_REQUEST: AppAny = {
  address: {
    value: '',
    unrestricted_value: '',
    data: {
      geo_lat: null,
      geo_lon: null,
      house_cadnum: null,
    },
  },
  taskId: null,
  companyId: null,
  extNumber: null,
  customer: null,
  customerPhone: null,
  res: null,
  legalType: 'PHYSICAL',
  managerId: null,
  workerId: null,
  workerGroupId: null,
  plannedDate: null,
  executedDate: null,
  sourceContent: {},
  workData: {},
  deviceId: null,
  description: '',
  reasonRejectionCode: null,
  reasonRejection: null,
  reasonDelayCount: null,
  onRevision: false,
  customers: [],
  statusCode: 'CREATED',
  serviceType: null,
  taskLocationCode: null,
  taskLocation: null,
};

export interface IAssignTo {
  id: number | string;
  type: 'USER' | 'GROUP';
}

export const serviceRequestsState = atom<IPageableDto<IServiceRequest>>({
  key: 'serviceRequestsState',
  default: getEmptyPage<IServiceRequest>(),
});

export const currentServiceRequestState = atom<IServiceRequest | null>({
  key: 'currentServiceRequestState',
  default: null,
});

export const serviceRequestActiveTabState = atom<{
  activeTab: 'general' | 'deviceInfo' | 'delayed';
  editMode?: boolean;
  onlyUpload?: boolean;
} | null>({
  key: 'serviceRequestActiveTabState',
  default: null,
});

export const editableWorkData = atom<{
  form: Record<string, string> | null;
  isDirty: boolean;
}>({
  key: 'editableWorkData',
  default: {
    form: null,
    isDirty: false,
  },
});

export const baseWorkData = atom<
  Record<
    string,
    {
      id: number;
      type: string;
      value: string;
      mandatory: boolean;
      inputType?: InputTypeEnum;
    }
  >
>({
  key: 'baseWorkData',
  default: {},
});

export const docsToReplaceState = atom<{oldSrc: string; file: File; docId: string}[]>({
  key: 'docsToReplaceState',
  default: [],
});

export const serviceRequestPageable = atom<IPageableTable>({
  key: 'serviceRequestPageable',
  default: {
    page: 0,
    size: localStorage.getItem('serviceRequestsPerPage')
      ? parseInt(localStorage.getItem('serviceRequestsPerPage') as string, 10)
      : 100,
  },
});

export const toolbarServiceRequestSearchString = atom<{id: string; extNumber: string; deviceNumberNew: string}>({
  key: 'toolbarServiceRequestSearchString',
  default: {
    id: '',
    extNumber: '',
    deviceNumberNew: '',
  },
});

export const toolbarServiceRequestSearchStringArchived = atom<boolean>({
  key: 'toolbarServiceRequestSearchStringArchived',
  default: false,
});

export const triggerReasonDelays = atom<number>({
  key: 'triggerReasonDelays',
  default: 0,
});

export const currentReasonDelay = atom<{delay: Record<string, AppAny> | null; localOrder: number | null}>({
  key: 'currentReasonDelay',
  default: {
    delay: null,
    localOrder: null,
  },
});

export const delayDocLoading = atom<boolean>({
  key: 'delayDocLoading',
  default: false,
});

export const serviceRequestsToAssignState = atom<IPageableDto<IServiceRequest>>({
  key: 'serviceRequestsToAssignState',
  default: getEmptyPage<IServiceRequest>(),
});

export const assignableServiceRequestsState = atom<{
  [key: number]: number[];
}>({
  key: 'assignableServiceRequestsState',
  default: {
    0: [],
  },
});

export const assignToState = atom<IAssignTo[]>({
  key: 'assignToState',
  default: [],
});

export const serviceRequestToAssignPageable = atom<IPageableTable>({
  key: 'serviceRequestToAssignPageable',
  default: {
    page: 0,
    size: localStorage.getItem('serviceRequestsToAssignPerPage')
      ? parseInt(localStorage.getItem('serviceRequestsToAssignPerPage') as string, 10)
      : 5,
  },
});

export const toolbarServiceRequestToAssignSearchString = atom<string>({
  key: 'toolbarServiceRequestToAssignSearchString',
  default: '',
});

export const serviceRequestsQueryTrigger = atom<number>({
  key: 'serviceRequestsQueryTrigger',
  default: 0,
});

export const serviceRequestsSortQueryTrigger = atom<{field: string; sort: 'asc' | 'desc' | null | undefined}[]>({
  key: 'serviceRequestsSortQueryTrigger',
  default: [],
});

export const serviceRequestsToAssignQueryTrigger = atom<number>({
  key: 'serviceRequestsToAssignQueryTrigger',
  default: 0,
});

export const serviceRequestsToAssignSortQueryTrigger = atom<{field: string; sort: 'asc' | 'desc' | null | undefined}[]>(
  {
    key: 'serviceRequestsToAssignSortQueryTrigger',
    default: [],
  },
);

export const expandedGroupsToAssign = atom<string[]>({
  key: 'expandedGroupsToAssign',
  default: [],
});

export const formTemplatesState = atom<Record<string, IContentTreeItem[]>>({
  key: 'formTemplatesState',
  default: {},
});

export const filesFormNameMapState = atom<Map<string, boolean>>({
  key: 'filesFormNameMapState',
  default: new Map(),
});

export const contentTreeFilesQueryTrigger = atom<number>({
  key: 'contentTreeFilesQueryTrigger',
  default: 0,
});

export const contentTreeQueryTrigger = atom<number>({
  key: 'contentTreeQueryTrigger',
  default: 0,
});
